import { useFeatureIsOn } from '@growthbook/growthbook-react'

import { useAppSelector } from '~/redux/hooks'
import { TopicId } from '~/dashboard/utils/learningPath.utils'
import LoaderSkeleton from '~/shared/components/LoaderSkeleton'
import { CATEGORY_COLORS } from '~/bootcamp/constants/bootcamp.const'
import { selectActiveCompetencyList } from '~/curriculum/slice/persistCurriculum.slice'
import { useGetCurriculumCountryCodeFromUrlWithoutPrefix } from '~/curriculum/hooks/useGetCurriculumCountryCode'

import { CurriculumCompetencyDataType } from '../types/curriculum.types'
import { selectCurriculumChangeLoading, selectGradeLevel } from '../slice/curriculum.slice'

import CurriculumCompetency from './CurriculumCompetency'

interface Props {
  uniqueSubjectId: string
  topicId: TopicId
  competencyList: CurriculumCompetencyDataType[]
  masteryTrackApplicable: boolean
}

export default function CurriculumCompetencyList({
  uniqueSubjectId,
  topicId,
  competencyList,
  masteryTrackApplicable,
}: Props): React.ReactElement {
  const countryCodeFromUrl = useGetCurriculumCountryCodeFromUrlWithoutPrefix()
  const level = useAppSelector(selectGradeLevel)
  const isLevelsExpanded = useFeatureIsOn('curriculum-levels-expanded')
  const activeCompetencyIds = useAppSelector(selectActiveCompetencyList)
  const curriculumChangeLoading = useAppSelector(selectCurriculumChangeLoading)
  const bgClass = CATEGORY_COLORS[topicId]?.bgClass

  return (
    <div className="flex justify-center flex-wrap max-w-screen-2xl mx-auto px-4 2xl:px-10 gap-12">
      {curriculumChangeLoading && <LoaderSkeleton className="h-28 w-full rounded-2.5 my-4" rows={3} />}
      {!curriculumChangeLoading &&
        competencyList?.map((competency, index: number) => {
          const competencyKey = `${index}_competency_${uniqueSubjectId}`
          const uniqueId = `${countryCodeFromUrl}_${level}_${competencyKey}`
          const isExpanded = activeCompetencyIds.includes(uniqueId)
          return (
            <div className="relative" key={competencyKey}>
              {masteryTrackApplicable && (
                <div
                  className={`absolute border-2 border-white -top-7 z-5 left-1/2 transform -translate-x-1/2 ${bgClass} rounded-full w-8 h-8 flex items-center justify-center shadow-md`}
                >
                  <span className="text-white font-bold">{index + 1}</span>
                </div>
              )}
              <CurriculumCompetency
                competency={competency}
                competencyKey={competencyKey}
                expandLevels={isExpanded || isLevelsExpanded}
                key={competencyKey}
                masteryTrackApplicable={masteryTrackApplicable}
                shouldScroll={(isExpanded || isLevelsExpanded) && activeCompetencyIds.length === 1}
                topicId={topicId}
                uniqueId={uniqueId}
              />
            </div>
          )
        })}
    </div>
  )
}
